import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { ArrowDown, ArrowUpRight, Camera, Sparkles } from "lucide-react";
import { C, EASE, DEFAULT_DECISIONS, RESUME_URL, media, scrollToId, useContent } from "./theme";
import { splitHeading } from "./Chrome";
import { useIsDesktop } from "./hooks";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII"];
const LINE = ["#E3B34C", "#E8798D", "#F29E4C", "#5FB3A5", "#8EA6E0", "#C99BE0", "#E3B34C"];
const NEXT_COLOR = "#F5C6CF";
const PTS = [[30, 150], [180, 150], [250, 80], [440, 80], [500, 140], [680, 140], [740, 70], [970, 70]];
const SEGS = PTS.slice(1).map((p, i) => Math.hypot(p[0] - PTS[i][0], p[1] - PTS[i][1]));
const TOTAL = SEGS.reduce((a, b) => a + b, 0);

function pointAt(t) {
  let d = Math.max(0, Math.min(1, t)) * TOTAL;
  for (let i = 0; i < SEGS.length; i++) {
    if (d <= SEGS[i] || i === SEGS.length - 1) {
      const f = SEGS[i] ? Math.min(1, d / SEGS[i]) : 0;
      const [a, b] = [PTS[i], PTS[i + 1]];
      return { x: a[0] + (b[0] - a[0]) * f, y: a[1] + (b[1] - a[1]) * f, a: (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI };
    }
    d -= SEGS[i];
  }
  return { x: 0, y: 0, a: 0 };
}

function pathBetween(t0, t1, steps = 40) {
  const pts = Array.from({ length: steps + 1 }, (_, k) => pointAt(t0 + ((t1 - t0) * k) / steps));
  return `M${pts.map((p) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" L")}`;
}

const stopT = (i, n) => 0.06 + (i * 0.88) / Math.max(1, n - 1);

function useNextStop() {
  const a = useContent("about");
  const ns = useContent("nextstop");
  return {
    primary: ns.primaryLabel || "Hold the doors",
    secondary: ns.secondaryLabel || "See my ticket (resume)",
    station: ns.station || a.nextStation || "Your company?",
    heading: ns.heading || a.nextHeading || "Next stop: your company.",
    body: ns.body || a.nextBody || "This train is bound for Brand Strategy. Doors are open for Brand, Strategy, Search and Product Marketing roles. Hop on, there's a seat by the window.",
  };
}

function Train({ x, y, a }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${a})`} data-testid="subway-train">
      <ellipse cx="34" cy="0" rx="22" ry="9" fill="#FFE7A8" opacity="0.28" />
      <rect x="-30" y="-13" width="60" height="26" rx="10" fill={C.cream} stroke={C.wine} strokeWidth="2" />
      <rect x="-30" y="5" width="60" height="3" fill={C.gold} />
      {[-22, -9, 4].map((wx) => <rect key={wx} x={wx} y="-8" width="10" height="9" rx="2" fill={C.burgundy} />)}
      <circle cx="21" cy="-3" r="5.5" fill={LINE[0]} />
      <text x="21" y="-0.5" textAnchor="middle" style={{ font: "700 7px 'JetBrains Mono', monospace", fill: C.wine }}>S</text>
      <circle cx="28" cy="7" r="2" fill="#FFE7A8" />
    </g>
  );
}

function SignPlate({ x, y, text, color, on }) {
  const w = Math.max(80, text.length * 9.4 + 40);
  return (
    <g transform={`translate(${x - w / 2} ${y})`} style={{ transition: "opacity 0.4s" }} opacity={on ? 1 : 0.72}>
      <rect width={w} height="30" rx="6" fill="#22060B" stroke={on ? color : "#ffffff22"} strokeWidth="1.2" />
      <rect width={w} height="3" rx="1.5" fill={color} />
      <circle cx="16" cy="16.5" r="5.5" fill={color} />
      <text x="29" y="21.5" style={{ font: "500 15px 'Inter', 'Helvetica Neue', sans-serif", fill: C.cream, letterSpacing: "0.02em" }}>{text}</text>
    </g>
  );
}

function Photo({ src, label, color, className = "" }) {
  return src ? (
    <img src={media(src)} alt={label} className={`object-cover ${className}`} />
  ) : (
    <div className={`flex flex-col items-center justify-center gap-2 ${className}`} style={{ background: `linear-gradient(160deg, ${color}40, ${C.rich})`, border: `1px dashed ${color}88` }} data-testid="chapter-photo-placeholder">
      <Camera size={20} style={{ color }} />
      <span className="ed-mono text-[8px] uppercase tracking-[0.3em] text-center px-2" style={{ color: `${C.cream}aa` }}>{label}</span>
    </div>
  );
}

function ChapterCard({ d, i }) {
  const color = LINE[i % LINE.length];
  return (
    <motion.div data-testid="decision-modal" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.55, ease: EASE }}
      className="grid grid-cols-[150px_1fr_2fr] [@media(max-height:860px)]:grid-cols-[120px_1fr_2fr] gap-8 xl:gap-12 items-start">
      <Photo src={d.photo} label={d.station || d.label} color={color} className="w-full aspect-[4/5] rounded-xl" />
      <div>
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full flex items-center justify-center ed-serif italic text-xs" style={{ background: color, color: C.wine }}>{ROMAN[i]}</span>
          <span className="ed-mono text-[10px] uppercase tracking-[0.3em]" style={{ color }}>{d.station || d.label}</span>
        </div>
        <h3 className="ed-serif text-4xl xl:text-5xl [@media(max-height:820px)]:text-4xl font-light leading-[1.05] mt-3" style={{ color: C.cream }}>{d.chapter}</h3>
        {d.lesson && <p className="ed-serif italic text-xl xl:text-2xl mt-3 leading-snug" style={{ color: C.blushDeep }}>“{d.lesson}”</p>}
      </div>
      <div className="grid grid-cols-3 gap-6">
        {[["The decision", d.decision], ["The risk", d.risk], ["The outcome", d.outcome]].map(([k, v]) => (
          <div key={k} className="pt-4" style={{ borderTop: `2px solid ${color}` }}>
            <p className="ed-mono text-[9px] uppercase tracking-[0.3em] mb-2" style={{ color: C.goldLight }}>{k}</p>
            <p className="text-base leading-relaxed" style={{ color: `${C.cream}e6` }}>{v}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function NextStopCard({ next, compact = false }) {
  const contact = useContent("contact");
  return (
    <motion.div data-testid="next-stop-card" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.55, ease: EASE }}
      className={`relative rounded-2xl overflow-hidden ${compact ? "p-6" : "p-8 xl:p-10 flex items-center justify-between gap-10 mr-24"}`} style={{ background: `linear-gradient(120deg, ${C.rich}, #3A0A12)`, border: `1px solid ${NEXT_COLOR}55` }}>
      <div>
        <p className="ed-mono text-[10px] uppercase tracking-[0.35em] flex items-center gap-2" style={{ color: NEXT_COLOR }}><Sparkles size={12} /> Now boarding · all aboard</p>
        <h3 className={`ed-serif font-light leading-tight mt-3 ${compact ? "text-3xl" : "text-4xl xl:text-5xl"}`} style={{ color: C.cream }}>{next.heading}</h3>
        <p className={`mt-3 leading-relaxed ${compact ? "text-sm" : "text-base max-w-xl"}`} style={{ color: `${C.cream}d0` }}>{next.body}</p>
      </div>
      <div className={`flex gap-3 shrink-0 ${compact ? "mt-5 flex-wrap" : "flex-col"}`}>
        <button data-testid="next-stop-hold-doors" onClick={() => scrollToId("contact")} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full ed-mono text-[10px] uppercase tracking-[0.25em] transition-transform duration-300 hover:-translate-y-0.5" style={{ background: NEXT_COLOR, color: C.wine }}>
          {next.primary} <ArrowUpRight size={13} />
        </button>
        <a data-testid="next-stop-resume" href={contact.resume || RESUME_URL} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full ed-mono text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-white/10" style={{ border: `1px solid ${C.cream}55`, color: C.cream }}>
          {next.secondary}
        </a>
      </div>
    </motion.div>
  );
}

function Announcement({ text }) {
  return (
    <div className="inline-flex items-center gap-3 px-4 py-2 rounded-md overflow-hidden" style={{ background: "#1C0509", border: `1px solid ${C.gold}33` }} data-testid="subway-announcement">
      <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#FFB547" }} />
      <AnimatePresence mode="wait">
        <motion.span key={text} initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -12, opacity: 0 }} transition={{ duration: 0.35 }} className="ed-mono text-[11px] uppercase tracking-[0.25em] whitespace-normal lg:whitespace-nowrap" style={{ color: "#FFB547" }}>
          {text}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

function SectionTitle({ eyebrow, head, ital, mobile }) {
  return (
    <div>
      <div className={`flex items-center gap-4 ${mobile ? "mb-5" : "mb-6 [@media(max-height:820px)]:mb-3"}`}>
        <span className="ed-mono text-xs tracking-[0.25em]" style={{ color: C.goldLight }}>01</span>
        <span className="h-px w-14" style={{ background: C.goldLight }} />
        <span className="ed-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: C.blushDeep }}>{eyebrow}</span>
      </div>
      <h2 className={`ed-serif font-light leading-[1.02] tracking-[-0.01em] ${mobile ? "text-4xl sm:text-5xl" : "text-7xl [@media(max-height:820px)]:text-6xl"}`} style={{ color: C.cream }}>{head} <em className="font-normal" style={{ color: C.blushDeep }}>{ital}</em></h2>
    </div>
  );
}

function SubwayDesktop({ items, head, ital, eyebrow }) {
  const ref = useRef(null);
  const next = useNextStop();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const t = useTransform(scrollYProgress, [0.04, 0.96], [0, 1]);
  const [pos, setPos] = useState(0);
  useMotionValueEvent(t, "change", setPos);
  const n = items.length + 1;
  const active = Array.from({ length: n }).reduce((acc, _, i) => (pos >= stopT(i, n) - 0.035 ? i : acc), 0);
  const isNext = active === n - 1;
  const train = pointAt(Math.min(pos, stopT(n - 1, n)));
  const goTo = (i) => {
    const el = ref.current;
    const span = el.offsetHeight - window.innerHeight;
    window.scrollTo({ top: el.offsetTop + span * (0.04 + stopT(i, n) * 0.92), behavior: "smooth" });
  };
  const skip = () => window.scrollTo({ top: ref.current.offsetTop + ref.current.offsetHeight, behavior: "smooth" });
  const label = (i) => (i === n - 1 ? next.station : items[i].station || items[i].label);
  const legs = Array.from({ length: n }).map((_, i) => [i === 0 ? 0 : stopT(i - 1, n), stopT(i, n)]);
  return (
    <section id="about" ref={ref} data-testid="decisions-about" className="relative" style={{ height: `${100 + n * 32}vh`, background: C.wine }}>
      <div className="sticky top-0 h-screen pt-[72px] flex flex-col overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{ backgroundImage: `linear-gradient(${C.cream} 1px, transparent 1px), linear-gradient(90deg, ${C.cream} 1px, transparent 1px)`, backgroundSize: "48px 48px" }} />
        <div className="relative max-w-[1440px] w-full mx-auto px-12 flex-1 min-h-0 flex flex-col justify-center gap-4 xl:gap-7 [@media(max-height:860px)]:gap-3 py-6">
          <div className="flex items-end justify-between gap-10">
            <SectionTitle eyebrow={eyebrow} head={head} ital={ital} />
            <div className="flex flex-col items-end gap-2">
              <Announcement text={isNext ? `Next stop · ${next.station.replace(/\?$/, "")}` : `Now arriving · ${label(active)}`} />
              <div className="flex items-center gap-4">
                <p className="ed-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: `${C.cream}80` }}>Scroll to ride · tap a stop</p>
                <button data-testid="backstory-skip" onClick={skip} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full ed-mono text-[9px] uppercase tracking-[0.3em] transition-colors duration-300 hover:bg-[#F2DADE] hover:text-[#4A0E17]" style={{ border: `1px solid ${C.cream}55`, color: C.cream }}>
                  Skip ahead <ArrowDown size={11} />
                </button>
              </div>
            </div>
          </div>
          <svg viewBox="0 0 1000 214" className="w-full h-auto max-h-[40vh] shrink-0 overflow-visible" data-testid="subway-map">
            <path d={pathBetween(0, stopT(n - 1, n), 120)} fill="none" stroke="#1C0509" strokeWidth="20" strokeLinecap="round" strokeLinejoin="round" />
            {legs.map(([a, b], i) => (
              <g key={i}>
                <path d={pathBetween(a, b)} fill="none" stroke={i === n - 1 ? NEXT_COLOR : LINE[i % LINE.length]} strokeWidth="11" strokeLinecap="round" strokeLinejoin="round"
                  strokeDasharray={i === n - 1 ? "2 16" : undefined} opacity={pos >= a ? 1 : 0.3} style={{ transition: "opacity 0.5s" }} className={i === n - 1 ? "subway-dash" : ""} />
              </g>
            ))}
            <circle cx={PTS[0][0]} cy={PTS[0][1]} r="7" fill={C.gold} stroke={C.wine} strokeWidth="3" />
            <text x={PTS[0][0]} y={PTS[0][1] - 16} textAnchor="middle" style={{ font: "500 9px 'JetBrains Mono', monospace", letterSpacing: "0.25em", fill: C.goldLight }}>HOME</text>
            {Array.from({ length: n }).map((_, i) => {
              const p = pointAt(stopT(i, n));
              const on = i === active;
              const last = i === n - 1;
              const color = last ? NEXT_COLOR : LINE[i % LINE.length];
              const below = p.y >= 112;
              return (
                <g key={i} onClick={() => goTo(i)} className="cursor-pointer" data-testid={last ? "decision-next" : `decision-${i}`}>
                  {on && <circle cx={p.x} cy={p.y} r="30" fill={color} opacity="0.18" className="subway-pulse" style={{ transformOrigin: `${p.x}px ${p.y}px` }} />}
                  <circle cx={p.x} cy={p.y} r={on ? 22 : 16} fill={on ? color : C.cream} stroke={on ? C.cream : color} strokeWidth="4" style={{ transition: "r 0.35s, fill 0.35s" }} />
                  <text x={p.x} y={p.y + 5} textAnchor="middle" style={{ font: `italic 600 ${on ? 17 : 14}px 'Cormorant Garamond', serif`, fill: C.wine }}>{last ? "?" : ROMAN[i]}</text>
                  <SignPlate x={p.x} y={below ? p.y + 30 : p.y - 62} text={label(i)} color={color} on={on} />
                </g>
              );
            })}
            <Train {...train} />
          </svg>
          <div className="min-h-[210px]">
            <AnimatePresence mode="wait">
              {isNext ? <NextStopCard key="next" next={next} /> : <ChapterCard key={active} d={items[active]} i={active} />}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function MobileStop({ d, i, open, onToggle, refCb }) {
  const color = LINE[i % LINE.length];
  return (
    <div ref={refCb} className="relative pb-9">
      <button data-testid={`decision-${i}`} onClick={() => onToggle(i)} className="w-full text-left">
        <span className="absolute -left-[50px] top-0 w-9 h-9 rounded-full flex items-center justify-center ed-serif italic text-sm font-semibold transition-colors duration-300" style={{ background: open ? color : C.cream, border: `3px solid ${open ? C.cream : color}`, color: C.wine }}>{ROMAN[i]}</span>
        <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded" style={{ background: "#22060B", borderTop: `2px solid ${color}` }}>
          <span className="w-2 h-2 rounded-full" style={{ background: color }} />
          <span className="text-[11px] tracking-wide" style={{ color: C.cream, fontFamily: "'Inter', sans-serif" }}>{d.station || d.label}</span>
        </span>
        <p className="ed-serif text-3xl leading-tight mt-2" style={{ color: open ? C.cream : `${C.cream}bb` }}>{d.chapter}</p>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease: EASE }} className="overflow-hidden" data-testid={`decision-modal-${i}`}>
            <div className="pt-4 space-y-4">
              <Photo src={d.photo} label={d.station || d.label} color={color} className="w-full aspect-[16/10] rounded-xl" />
              {d.lesson && <p className="ed-serif italic text-xl leading-snug" style={{ color: C.blushDeep }}>“{d.lesson}”</p>}
              {[["The decision", d.decision], ["The risk", d.risk], ["The outcome", d.outcome]].map(([k, v]) => (
                <div key={k} className="pl-3" style={{ borderLeft: `2px solid ${color}` }}>
                  <p className="ed-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: C.goldLight }}>{k}</p>
                  <p className="text-base leading-relaxed mt-1" style={{ color: `${C.cream}e0` }}>{v}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SubwayMobile({ items, head, ital, eyebrow }) {
  const next = useNextStop();
  const [opened, setOpened] = useState(() => new Set([0]));
  const [trainY, setTrainY] = useState(0);
  const lineRef = useRef(null);
  const stopRefs = useRef([]);
  const autoOpened = useRef(new Set([0]));
  useEffect(() => {
    const onScroll = () => {
      const line = lineRef.current;
      if (!line) return;
      const r = line.getBoundingClientRect();
      const y = Math.min(Math.max(window.innerHeight * 0.55 - r.top, 0), r.height);
      setTrainY(y);
      const crossed = stopRefs.current.reduce((acc, el, i) => (el && el.offsetTop <= y ? i : acc), -1);
      const fresh = [];
      for (let i = 0; i <= crossed; i++) if (!autoOpened.current.has(i)) { autoOpened.current.add(i); fresh.push(i); }
      if (fresh.length) setOpened((o) => new Set([...o, ...fresh]));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, []);
  const toggle = (i) => setOpened((o) => { const s = new Set(o); if (s.has(i)) s.delete(i); else s.add(i); return s; });
  const [tops, setTops] = useState([]);
  useEffect(() => {
    const measure = () => setTops((prev) => {
      const nextTops = stopRefs.current.map((el) => (el ? el.offsetTop : 0));
      return nextTops.join() === prev.join() ? prev : nextTops;
    });
    measure();
    const ro = new ResizeObserver(measure);
    if (lineRef.current) ro.observe(lineRef.current);
    return () => ro.disconnect();
  }, []);
  const segTop = (i) => (tops[i] ?? 0) + 18;
  return (
    <section id="about" data-testid="decisions-about" className="relative min-h-screen py-24 px-6" style={{ background: C.wine }}>
      <SectionTitle eyebrow={eyebrow} head={head} ital={ital} mobile />
      <div className="mt-6 mb-10"><Announcement text="Line S · express to brand strategy" /></div>
      <div ref={lineRef} className="relative pl-[50px]" data-testid="subway-map">
        <span className="absolute left-[13px] top-2 w-[10px] rounded-full" style={{ height: segTop(items.length), background: "#1C0509" }} />
        {items.map((_, i) => {
          const top = i === 0 ? 8 : segTop(i - 1);
          const h = Math.max(segTop(i) - top, 0);
          return <span key={i} className="absolute left-[15px] w-[6px] rounded-full transition-opacity duration-500" style={{ top, height: h, background: LINE[i % LINE.length], opacity: trainY >= top ? 1 : 0.3 }} />;
        })}
        <span className="absolute left-[15px] w-[6px] rounded-full subway-dash-v" style={{ top: segTop(items.length - 1), height: Math.max(segTop(items.length) - segTop(items.length - 1), 0), backgroundImage: `repeating-linear-gradient(to bottom, ${NEXT_COLOR} 0 4px, transparent 4px 14px)` }} />
        <span data-testid="subway-train" className="absolute left-[6px] w-[24px] h-[38px] rounded-[9px] z-10 -translate-y-1/2 transition-[top] duration-150 ease-out flex flex-col items-center justify-center gap-[3px]" style={{ top: trainY, background: C.cream, border: `2px solid ${C.wine}`, boxShadow: `0 0 14px #FFE7A855` }}>
          <span className="w-[12px] h-[6px] rounded-sm" style={{ background: C.burgundy }} />
          <span className="w-[12px] h-[6px] rounded-sm" style={{ background: C.burgundy }} />
          <span className="w-[8px] h-[3px] rounded-full" style={{ background: C.gold }} />
        </span>
        {items.map((d, i) => <MobileStop key={i} d={d} i={i} open={opened.has(i)} onToggle={toggle} refCb={(el) => (stopRefs.current[i] = el)} />)}
        <div className="relative pb-2" data-testid="decision-next" ref={(el) => (stopRefs.current[items.length] = el)}>
          <span className="absolute -left-[50px] top-0 w-9 h-9 rounded-full flex items-center justify-center ed-serif italic text-base font-semibold" style={{ background: C.cream, border: `3px dashed ${NEXT_COLOR}`, color: C.wine }}>?</span>
          <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded mb-4" style={{ background: "#22060B", borderTop: `2px solid ${NEXT_COLOR}` }}>
            <span className="w-2 h-2 rounded-full" style={{ background: NEXT_COLOR }} />
            <span className="text-[11px] tracking-wide" style={{ color: C.cream, fontFamily: "'Inter', sans-serif" }}>{next.station}</span>
          </span>
          <NextStopCard next={next} compact />
        </div>
      </div>
    </section>
  );
}

export function SubwayBackstory() {
  const about = useContent("about");
  const dec = useContent("decisions");
  const items = (dec.items?.length ? dec.items : DEFAULT_DECISIONS).slice(0, 6);
  const desktop = useIsDesktop();
  const [head, ital] = splitHeading(about.heading, "Five decisions,", "one direction.");
  const props = { items, head, ital, eyebrow: about.eyebrow || "Backstory" };
  return desktop ? <SubwayDesktop {...props} /> : <SubwayMobile {...props} />;
}
